import React, { useEffect, useMemo } from "react"

import * as Sentry from "@sentry/react"
import { format, isValid, parse } from "date-fns"
import { useSearchParams } from "react-router-dom"

import { Tabs, TabsContent } from "@/components/ui/tabs"
import { useAuth } from "@/contexts/AuthContext"

import RoleTeamGuard from "../components/guards/RoleTeamGuard"
import CollectionScheduleContent from "../components/sheriff/tabs/CollectionScheduleContent"
import DeliveryScheduleContent from "../components/sheriff/tabs/DeliveryScheduleContent"
import IncentivesContent from "../components/sheriff/tabs/IncentivesContent"
import InternalScheduleContent from "../components/sheriff/tabs/InternalScheduleContent"
import ReWavingContent from "../components/sheriff/tabs/ReWavingContent"
import SheriffTabsList from "../components/sheriff/tabs/SheriffTabsList"
import SortationScheduleContent from "../components/sheriff/tabs/SortationScheduleContent"
import TicketsContent from "../components/sheriff/tabs/TicketsContent"
import TimesheetsContent from "../components/sheriff/tabs/TimesheetsContent"
import DashboardLayout from "../layouts/DashboardLayout"

const { logger } = Sentry
const sentryVerbose = import.meta.env.VITE_SENTRY_VERBOSE_UI === "true"

type SheriffTab =
  | "delivery"
  | "collection"
  | "sortation"
  | "internal"
  | "timesheets"
  | "tickets"
  | "re-waving"
  | "incentives"
const INITIAL_TAB: SheriffTab = "delivery"
const ALL_TABS: SheriffTab[] = [
  "delivery",
  "collection",
  "sortation",
  "internal",
  "timesheets",
  "tickets",
  "re-waving",
  "incentives",
]
// Tabs only Admin/Manager can access (delivery & tickets are also available to DSP).
const MANAGER_ONLY_TABS = new Set<SheriffTab>(["collection", "sortation", "internal", "timesheets"])
const OPS_TABS = new Set<SheriffTab>(["re-waving"])
const DATE_PARAM_FORMAT = "yyyy-MM-dd"

// Resolve the tab from the URL, falling back to delivery for unknown values or
// tabs the current role can't access (so deep links can't land on a blank tab).
const resolveTab = (
  raw: string | null,
  role?: string,
  team?: string,
  teamPermissions: string[] = [],
): SheriffTab => {
  const tab = ALL_TABS.find((t) => t === raw)
  if (!tab) {
    return INITIAL_TAB
  }
  const canAccessManagerTabs = role === "Admin" || role === "Manager"
  if (MANAGER_ONLY_TABS.has(tab) && !canAccessManagerTabs) {
    return INITIAL_TAB
  }
  if (
    OPS_TABS.has(tab) &&
    team !== "Central Operations" &&
    team !== "Driver Operations" &&
    team !== "Admin" &&
    team !== "Depot"
  ) {
    return INITIAL_TAB
  }
  if (tab === "incentives" && !teamPermissions.includes("view.page.incentives")) {
    return INITIAL_TAB
  }
  return tab
}

const Sheriff: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { claims } = useAuth()
  const role = claims?.user_role
  const team = claims?.user_team
  const teamPermissions = claims?.team_permissions

  // The URL is the source of truth for the active tab and selected date so the
  // current view can be shared / deep-linked and survives a refresh.
  const activeTab = resolveTab(searchParams.get("tab"), role, team, teamPermissions)

  const dateParam = searchParams.get("date")
  const selectedDate = useMemo(() => {
    if (dateParam) {
      const parsed = parse(dateParam, DATE_PARAM_FORMAT, new Date())
      if (isValid(parsed)) return parsed
    }
    return new Date()
  }, [dateParam])

  // Normalise the URL if it points at an unknown/unauthorised tab.
  const tabParam = searchParams.get("tab")
  useEffect(() => {
    if (tabParam && tabParam !== activeTab) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set("tab", activeTab)
          return next
        },
        { replace: true },
      )
    }
  }, [tabParam, activeTab, setSearchParams])

  const handleTabChange = (value: string) => {
    const newTab = resolveTab(value, role, team, teamPermissions)
    if (sentryVerbose) {
      Sentry.startSpan({ op: "ui.click", name: "Sheriff Tab Change" }, (span) => {
        span.setAttribute("from_tab", activeTab)
        span.setAttribute("to_tab", newTab)
        logger.debug(logger.fmt`Tab changed from ${activeTab} to ${newTab}`)
      })
    } else {
      Sentry.addBreadcrumb({
        category: "ui",
        level: "info",
        message: "Sheriff tab change",
        data: { from: activeTab, to: newTab },
      })
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set("tab", newTab)
        return next
      },
      { replace: true },
    )
  }

  const handleSelectedDateChange = (date: Date) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set("date", format(date, DATE_PARAM_FORMAT))
        return next
      },
      { replace: true },
    )
  }

  // Track page load once
  useEffect(() => {
    Sentry.startSpan(
      {
        op: "page.load",
        name: "Sheriff Page Load",
      },
      (span) => {
        span.setAttribute("initial_tab", INITIAL_TAB)
        if (sentryVerbose) {
          logger.info("Sheriff page loaded", { activeTab: INITIAL_TAB })
        }
      },
    )
  }, [])

  return (
    <DashboardLayout>
      <Tabs value={activeTab} className="mb-6" onValueChange={handleTabChange}>
        <SheriffTabsList activeTab={activeTab} onValueChange={handleTabChange} />

        {/* Delivery: Admin, DSP, Manager */}
        <RoleTeamGuard roles={["Admin", "DSP", "Manager"]} fallback={null}>
          <TabsContent value="delivery">
            <DeliveryScheduleContent
              selectedDate={selectedDate}
              onSelectedDateChange={handleSelectedDateChange}
            />
          </TabsContent>
        </RoleTeamGuard>

        {/* Collection: Admin, Manager only */}
        <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
          <TabsContent value="collection">
            <CollectionScheduleContent
              selectedDate={selectedDate}
              onSelectedDateChange={handleSelectedDateChange}
            />
          </TabsContent>
        </RoleTeamGuard>

        {/* Sortation: Admin, Manager only */}
        <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
          <TabsContent value="sortation">
            <SortationScheduleContent
              selectedDate={selectedDate}
              onSelectedDateChange={handleSelectedDateChange}
            />
          </TabsContent>
        </RoleTeamGuard>

        {/* Internal: Admin, Manager only */}
        <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
          <TabsContent value="internal">
            <InternalScheduleContent
              selectedDate={selectedDate}
              onSelectedDateChange={handleSelectedDateChange}
            />
          </TabsContent>
        </RoleTeamGuard>

        {/* Timesheets: Admin, Manager only */}
        <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
          <TabsContent value="timesheets">
            <TimesheetsContent />
          </TabsContent>
        </RoleTeamGuard>

        {/* Tickets: Admin, DSP, Manager */}
        <RoleTeamGuard roles={["Admin", "DSP", "Manager"]} fallback={null}>
          <TabsContent value="tickets">
            <TicketsContent />
          </TabsContent>
        </RoleTeamGuard>

        <RoleTeamGuard
          teams={["Admin", "Central Operations", "Driver Operations", "Depot"]}
          fallback={null}
        >
          <TabsContent value="re-waving">
            <ReWavingContent />
          </TabsContent>
        </RoleTeamGuard>

        <RoleTeamGuard permissions={["view.page.incentives"]} fallback={null}>
          <TabsContent value="incentives">
            <IncentivesContent />
          </TabsContent>
        </RoleTeamGuard>
      </Tabs>
    </DashboardLayout>
  )
}

export default Sheriff
